import {
  memo, useEffect, useState,
} from '../../lib/teact/teact';

import buildClassName from '../../util/buildClassName';

import useLastCallback from '../../hooks/useLastCallback';

import Icon from '../common/icons/Icon';

import styles from './PromptEditorModal.module.scss';

export type PromptKey = 'institutional' | 'rag' | 'formatting';

export interface PromptsConfig {
  institutional_prompt: string;
  rag_context_prompt: string;
  formatting_prompt: string;
}

type OwnProps = {
  isOpen: boolean;
  initialPromptKey?: PromptKey;
  prompts: PromptsConfig;
  isSaving?: boolean;
  onClose: NoneToVoidFunction;
  onSave: (payload: PromptsConfig) => void;
  onResetPrompt?: (key: PromptKey) => void;
};

const PROMPT_METADATA: Record<PromptKey, {
  title: string;
  tabLabel: string;
  subtitle: string;
  description: string;
  placeholder: string;
}> = {
  institutional: {
    title: '1. Prompt Institucional (Identidad, Reglas Críticas y Canales)',
    tabLabel: '🏛️ 1. Identidad Institucional',
    subtitle: 'Reglas de oro, modalidad de clases y canal oficial',
    description: 'Define la voz oficial del Asistente de Posgrado EGEMC, la prohibición de grabar clases '
      + '(100% presencial sincrónico), el canal oficial exclusivo de Telegram y políticas arancelarias.',
    placeholder: 'Escribe las directivas de identidad institucional...',
  },
  rag: {
    title: '2. Directivas RAG y Anti-Alucinación (Grounding Fáctico)',
    tabLabel: '📚 2. Directivas RAG',
    subtitle: 'Fidelidad fáctica y cálculo de fechas a Enero',
    description: 'Obliga a la IA a basarse de forma fáctica en el fragmento recuperado de la base de '
      + 'conocimiento y a calcular dinámicamente los meses faltantes hacia enero para programas cerrados.',
    placeholder: 'Escribe las directivas de recuperación y anti-alucinación...',
  },
  formatting: {
    title: '3. Formato y Estilo de Respuesta para Telegram',
    tabLabel: '📱 3. Estilo Telegram',
    subtitle: 'Estructura móvil, emojis y llamadas a la acción',
    description: 'Instruye el uso de párrafos cortos, negritas en fechas/costos indispensables, viñetas '
      + 'visuales, emojis sanitarios sobrios y despedidas cordiales con llamada a la acción.',
    placeholder: 'Escribe las instrucciones de formato y estilo...',
  },
};

const PromptEditorModal = ({
  isOpen,
  initialPromptKey = 'institutional',
  prompts,
  isSaving,
  onClose,
  onSave,
  onResetPrompt,
}: OwnProps) => {
  const [selectedKey, setSelectedKey] = useState<PromptKey>(initialPromptKey);
  const [localPrompts, setLocalPrompts] = useState<PromptsConfig>(prompts);

  useEffect(() => {
    if (!isOpen) return;
    setLocalPrompts(prompts);
    if (initialPromptKey) {
      setSelectedKey(initialPromptKey);
    }
  }, [isOpen, prompts, initialPromptKey]);

  const currentFieldMap: Record<PromptKey, keyof PromptsConfig> = {
    institutional: 'institutional_prompt',
    rag: 'rag_context_prompt',
    formatting: 'formatting_prompt',
  };

  const currentField = currentFieldMap[selectedKey];
  const currentText = localPrompts[currentField] || '';
  const currentMeta = PROMPT_METADATA[selectedKey];

  const handleTextChange = useLastCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setLocalPrompts((prev) => ({
      ...prev,
      [currentField]: val,
    }));
  });

  const handleResetCurrent = useLastCallback(() => {
    if (window.confirm(`¿Deseas restablecer el prompt "${currentMeta.title}" a su valor predeterminado de fábrica?`)) {
      onResetPrompt?.(selectedKey);
    }
  });

  const handleSave = useLastCallback(() => {
    onSave(localPrompts);
  });

  if (!isOpen) return undefined;

  const charCount = currentText.length;
  const wordCount = currentText.trim() ? currentText.trim().split(/\s+/).length : 0;
  const lineCount = currentText ? currentText.split('\n').length : 0;

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerInfo}>
            <div className={styles.headerIcon}>
              <Icon name="ai" />
            </div>
            <div>
              <h3 className={styles.headerTitle}>Editor de Directivas y Prompts en Extenso</h3>
              <p className={styles.headerSubtitle}>
                Ajuste y perfeccionamiento de las instrucciones centrales del Asistente de IA (DeepSeek).
              </p>
            </div>
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Cerrar"
          >
            <Icon name="close" />
          </button>
        </div>

        {/* Selector de Pestañas */}
        <div className={styles.tabsBar}>
          {(['institutional', 'rag', 'formatting'] as PromptKey[]).map((key) => {
            const meta = PROMPT_METADATA[key];
            const isActive = selectedKey === key;
            return (
              <button
                key={key}
                type="button"
                className={buildClassName(styles.tabItem, isActive && styles.tabItemActive)}
                onClick={() => setSelectedKey(key)}
              >
                {meta.tabLabel}
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div className={styles.body}>
          {/* Barra Informativa */}
          <div className={styles.promptInfoBar}>
            <div className={styles.promptInfoText}>
              <span className={styles.promptInfoTitle}>{currentMeta.title}</span>
              <span className={styles.promptInfoDesc}>{currentMeta.description}</span>
            </div>
            <div className={styles.statsChips}>
              <span className={styles.statBadge}>
                {charCount}
                {' '}
                caracteres
              </span>
              <span className={styles.statBadge}>
                {wordCount}
                {' '}
                palabras
              </span>
              <span className={styles.statBadge}>
                {lineCount}
                {' '}
                líneas
              </span>
            </div>
          </div>

          {/* Editor Textarea en Extenso */}
          <div className={styles.editorContainer}>
            <textarea
              className={styles.textarea}
              value={currentText}
              onChange={handleTextChange}
              placeholder={currentMeta.placeholder}
              rows={16}
              spellCheck={false}
            />
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <div>
            {onResetPrompt && (
              <button
                type="button"
                className={styles.btnResetSingle}
                onClick={handleResetCurrent}
                disabled={isSaving}
              >
                Restablecer este prompt a fábrica
              </button>
            )}
          </div>

          <div className={styles.footerButtons}>
            <button
              type="button"
              className={styles.btnCancel}
              onClick={onClose}
              disabled={isSaving}
            >
              Cancelar
            </button>
            <button
              type="button"
              className={styles.btnSave}
              onClick={handleSave}
              disabled={isSaving}
            >
              <Icon name="check" />
              <span>{isSaving ? 'Guardando...' : 'Guardar Directivas'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(PromptEditorModal);
